import React, { useState } from "react";
import { useLang } from "@/i18n";
import { createContact } from "@/api";
import { CONTACT, SOCIAL, waLink } from "@/config";
import { toast } from "sonner";
import { Mail, MessageCircle, MapPin, Clock, Instagram, Facebook } from "lucide-react";

export default function ContactPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", tour_interest: "", tentative_date: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(lang === "es" ? "Completa nombre, email y mensaje" : "Please fill name, email and message");
      return;
    }
    setSending(true);
    try {
      await createContact(form);
      toast.success(t("contact.thanks"));
      setForm({ name: "", email: "", whatsapp: "", tour_interest: "", tentative_date: "", message: "" });
    } catch {
      toast.error(lang === "es" ? "Error, intenta de nuevo" : "Error, try again");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14">
        <div className="md:col-span-5">
          <p className="eyebrow mb-5">{t("contact.kicker")}</p>
          <h1 className="font-serif-hc font-light text-5xl md:text-6xl leading-[1.02] text-[#F5F2EA]">
            {t("contact.title")}
          </h1>

          <div className="mt-12 space-y-6">
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="contact-wa" className="flex items-start gap-4 group">
              <span className="w-10 h-10 border border-[#1E2B25] group-hover:border-[#C4A661] flex items-center justify-center text-[#25D366]"><MessageCircle size={16}/></span>
              <div>
                <p className="eyebrow mb-1">WhatsApp</p>
                <p className="text-[#F5F2EA] group-hover:text-[#C4A661] transition">{CONTACT.WHATSAPP_DISPLAY}</p>
              </div>
            </a>
            <a href={`mailto:${CONTACT.EMAIL}`} className="flex items-start gap-4 group">
              <span className="w-10 h-10 border border-[#1E2B25] group-hover:border-[#C4A661] flex items-center justify-center text-[#C4A661]"><Mail size={16}/></span>
              <div>
                <p className="eyebrow mb-1">Email</p>
                <p className="text-[#F5F2EA] group-hover:text-[#C4A661] transition">{CONTACT.EMAIL}</p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 border border-[#1E2B25] flex items-center justify-center text-[#C4A661]"><MapPin size={16}/></span>
              <div>
                <p className="eyebrow mb-1">{t("common.location")}</p>
                <p className="text-[#F5F2EA]">{CONTACT.LOCATION[lang]}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 border border-[#1E2B25] flex items-center justify-center text-[#C4A661]"><Clock size={16}/></span>
              <div>
                <p className="eyebrow mb-1">{lang === "es" ? "Horarios" : "Hours"}</p>
                <p className="text-[#F5F2EA]">{CONTACT.HOURS[lang]}</p>
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <a href={SOCIAL.INSTAGRAM_URL} target="_blank" rel="noreferrer" className="text-[#F5F2EA] hover:text-[#C4A661]"><Instagram size={18}/></a>
              <a href={SOCIAL.FACEBOOK_URL} target="_blank" rel="noreferrer" className="text-[#F5F2EA] hover:text-[#C4A661]"><Facebook size={18}/></a>
              <a href={SOCIAL.TIKTOK_URL} target="_blank" rel="noreferrer" className="text-[#F5F2EA] hover:text-[#C4A661] text-sm">TikTok</a>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="md:col-span-6 md:col-start-7 bg-[#0C1310] border border-[#1E2B25] p-8 md:p-12 space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-2 gap-5">
            <label className="block">
              <span className="eyebrow block mb-2">{t("contact.name")}</span>
              <input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-3.5 text-[#F5F2EA]" />
            </label>
            <label className="block">
              <span className="eyebrow block mb-2">{t("contact.email")}</span>
              <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-3.5 text-[#F5F2EA]" />
            </label>
          </div>
          <label className="block">
            <span className="eyebrow block mb-2">{t("contact.whatsapp")}</span>
            <input data-testid="contact-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-3.5 text-[#F5F2EA]" />
          </label>
          <div className="grid grid-cols-2 gap-5">
            <label className="block">
              <span className="eyebrow block mb-2">{t("contact.tour_interest")}</span>
              <input data-testid="contact-tour" value={form.tour_interest} onChange={(e) => set("tour_interest", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-3.5 text-[#F5F2EA]" />
            </label>
            <label className="block">
              <span className="eyebrow block mb-2">{t("contact.date")}</span>
              <input data-testid="contact-date" type="date" value={form.tentative_date} onChange={(e) => set("tentative_date", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-3.5 text-[#F5F2EA]" />
            </label>
          </div>
          <label className="block">
            <span className="eyebrow block mb-2">{t("contact.message")}</span>
            <textarea data-testid="contact-message" rows="5" value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full bg-transparent border border-[#1E2B25] focus:border-[#C4A661] outline-none p-3.5 text-[#F5F2EA] resize-none" />
          </label>
          <button data-testid="contact-submit" type="submit" disabled={sending} className="hc-btn-primary w-full mt-2">
            {sending ? "…" : t("contact.cta")}
          </button>
        </form>
      </div>
    </div>
  );
}
